import { useState } from 'react';
import { Button, Center, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronRightIcon, ChevronLeftIcon, XIcon } from 'lucide-react';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { CustomStepper } from '@duke-hq/ui/components/StepWizard/CustomStepper';
import { useStep } from '@duke-hq/ui/components/StepWizard/useStep';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { PlacementDetails } from './PlacementDetails';
import { UnitsAndNotes } from './UnitsAndNotes';
import { Summary } from './Summary';
import { buildPayload } from './payload';
import {
  isPlacementDetailsValid,
  emptyPlacementDetails,
  type PlacementDetailsState,
  type PlacementContract,
} from './types';
import { testIds } from './testIds';

const STEP_TITLES = [() => t`Placement Details`, () => t`Units and notes`, () => t`Summary`];
const SUMMARY_STEP = STEP_TITLES.length - 1;

type Student = { fullName: string; courseLabel: string; usi: string | null };

function ModalBody({
  courseEnrolmentId,
  student,
  contract: liveContract,
  onClose,
  onSuccess,
}: {
  courseEnrolmentId: number;
  student: Student;
  contract: PlacementContract;
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  const [contract] = useState(liveContract);
  const { currentStep, goToNextStep, goToPrevStep, setStep } = useStep({
    maxStep: SUMMARY_STEP,
    initialStep: 0,
  });
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const [details, setDetails] = useState<PlacementDetailsState>(emptyPlacementDetails);

  const {
    data: availableUnits,
    isLoading: unitsLoading,
    isError: unitsError,
  } = trpcAdmin.workPlacements.placements.availableUnits.useQuery({ courseEnrolmentId });
  const hasAvailableUnits = availableUnits != null && availableUnits.length > 0;

  const create = trpcAdmin.workPlacements.placements.createWorkPlacement.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Placement created` });
      void utils.workPlacements.placements.journeyByEnrolment.invalidate();
      onSuccess();
      onClose();
    },
    onError: (error) => {
      void utils.workPlacements.placements.journeyByEnrolment.invalidate();
      toast({ status: 'error', title: t`Failed to create placement`, description: error.message });
    },
  });

  const nextDisabled = currentStep === 0 && !isPlacementDetailsValid(details);

  const stepper = (
    <Stack direction={{ base: 'column', md: 'row' }} spacing={0} px={6} pt={6} w="full">
      {STEP_TITLES.map((title, id) => (
        <CustomStepper
          key={title()}
          cursor="pointer"
          onClick={() => {
            if (currentStep > id) {
              setStep(id);
            }
          }}
          title={title()}
          isActive={currentStep === id}
          isCompleted={currentStep > id}
          isFirstStep={id === 0}
          isLastStep={id === SUMMARY_STEP}
        />
      ))}
    </Stack>
  );

  if (unitsLoading || unitsError || !hasAvailableUnits) {
    return (
      <FormMainContainer>
        <FormContentContainer>
          {unitsLoading ? (
            <Center w="full" py={10}>
              <Spinner />
            </Center>
          ) : (
            <Text w="full" color={unitsError ? 'danger' : 'fg.muted'}>
              {unitsError ? (
                <Trans>Unable to load the units for this enrolment. Close and try again.</Trans>
              ) : (
                <Trans>No outstanding work placement units for this enrolment.</Trans>
              )}
            </Text>
          )}
        </FormContentContainer>
        <FormFooterContainer>
          <Button
            variant="tertiary"
            onClick={onClose}
            leftIcon={<XIcon />}
            data-testid={testIds.backButton}
          >
            <Trans>Close</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    );
  }

  return (
    <FormMainContainer>
      {stepper}

      <FormContentContainer>
        {currentStep === 0 ? (
          <PlacementDetails contract={contract} value={details} onChange={setDetails} />
        ) : null}

        {currentStep === 1 ? (
          <UnitsAndNotes
            units={availableUnits ?? []}
            notes={details.notes}
            onNotesChange={(notes) => setDetails({ ...details, notes })}
          />
        ) : null}

        {currentStep === SUMMARY_STEP ? (
          <Summary
            student={student}
            contract={contract}
            details={details}
            onEdit={() => setStep(0)}
          />
        ) : null}
      </FormContentContainer>

      {currentStep < SUMMARY_STEP ? (
        <FormFooterContainer>
          <Button
            onClick={goToNextStep}
            isDisabled={nextDisabled}
            rightIcon={<ChevronRightIcon />}
            data-testid={testIds.nextButton}
          >
            <Trans>Next</Trans>
          </Button>
          <Button
            variant="tertiary"
            onClick={currentStep === 0 ? onClose : goToPrevStep}
            leftIcon={currentStep === 0 ? <XIcon /> : <ChevronLeftIcon />}
            data-testid={testIds.backButton}
          >
            {currentStep === 0 ? <Trans>Cancel</Trans> : <Trans>Back</Trans>}
          </Button>
        </FormFooterContainer>
      ) : (
        <FormFooterContainer>
          <Button
            onClick={() => create.mutate(buildPayload(courseEnrolmentId, contract.id, details))}
            isLoading={create.isPending}
            data-testid={testIds.createButton}
          >
            <Trans>Create placement</Trans>
          </Button>
          <Button
            variant="tertiary"
            onClick={goToPrevStep}
            leftIcon={<ChevronLeftIcon />}
            data-testid={testIds.backButton}
          >
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      )}
    </FormMainContainer>
  );
}

export function CreatePlacementModal({
  isOpen,
  onClose,
  courseEnrolmentId,
  student,
  contract,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  courseEnrolmentId: number;
  student: Student;
  contract: PlacementContract;
  onSuccess: () => void;
}): React.ReactElement {
  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      modalContentHeight="auto"
      modalContent={
        <ModalBody
          courseEnrolmentId={courseEnrolmentId}
          student={student}
          contract={contract}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      }
    />
  );
}
